import {
  useState,
  ChangeEvent,
  MouseEvent,
  FormEvent,
} from 'react'
import { OrderStatusChecker } from '@sh24/ui-components'
import useTranslations from '../../../utils/use-translations'
import notifyError from '../../../services/exception-notifier/exception-notifier'

interface OrderStatusCheckerProps {
  title: string
  inputPlaceholder: string
  ctaText: string
  statusRefused: string
  statusPreparing: string
  statusReviewed: string
  statusDispatchingSoon: string
  statusDispatched: string
  statusUnaccountedFor: string
  statusConfirmedDelivery: string
  statusProcessing: string
  statusUnknown: string
}

const validate = (value: string, translations: Record<string, string>): string[] => {
  const errors = []
  if (!value) errors.push(translations['errors.required'])

  return errors
}

const OrderStatusCheckerModule = ({
  title,
  inputPlaceholder,
  ctaText,
  statusRefused,
  statusPreparing,
  statusReviewed,
  statusDispatchingSoon,
  statusDispatched,
  statusUnaccountedFor,
  statusConfirmedDelivery,
  statusProcessing,
  statusUnknown,
}: OrderStatusCheckerProps) => {
  const statusCopyMap: Record<string, string> = {
    dispatched: statusDispatched,
    dispatching_soon: statusDispatchingSoon,
    refused: statusRefused,
    preparing: statusPreparing,
    reviewed: statusReviewed,
    unaccounted_for: statusUnaccountedFor,
    confirmed_delivery: statusConfirmedDelivery,
    processing: statusProcessing,
    unknown: statusUnknown,
  }

  const translations = useTranslations()
  const [inputValue, setInputValue] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [status, setStatus] = useState('')
  const [fetching, setFetching] = useState(false)

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrors([])
    setInputValue(e.target.value)
  }

  const fetchStatus = async () => {
    setFetching(true)
    try {
      const res = await fetch(`/api/order-status?sh24Uid=${inputValue}`)

      if (!res.ok) {
        setErrors([translations['errors.invalidOrderNumber']])
        setFetching(false)
        return
      }

      const json = await res.json()
      setStatus(statusCopyMap[json.orderStatus])
      setFetching(false)
    } catch (err) {
      notifyError(err)
      setErrors([translations['errors.somethingWentWrong']])
      setFetching(false)
    }
  }

  const handleSubmit = (e: MouseEvent | FormEvent) => {
    e.preventDefault()
    if (fetching) return

    setStatus('')

    const inputErrors = validate(inputValue, translations)

    if (inputErrors.length) {
      setErrors(inputErrors)
      return
    }

    fetchStatus()
  }

  return (
    <div className="row row-centered">
      <div className="col col-12 col-md-6 col-lg-4">
        <div>
          <OrderStatusChecker
            title={title}
            errors={errors}
            handleSubmit={handleSubmit}
            inputPlaceholder={inputPlaceholder}
            onInputChange={onInputChange}
            inputValue={inputValue}
            ctaText={ctaText}
            fetching={fetching}
            status={status}
          />
        </div>
      </div>
    </div>
  )
}

export default OrderStatusCheckerModule
